// Signed-out pages. Links in emails carry their token in the URL fragment (never sent to a server);
// these pages read it and POST it to the API.

import type { InvitationInfo, Me, SignInResult } from '@cck/schema';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useNavigate, useSearchParams } from 'react-router';
import { Alert, Button, Field, Input, LinkButton, Logo, Select, Spinner, ThemeToggle } from '../../components/ui.tsx';
import { setLanguage, useI18n } from '../../i18n/index.ts';
import { api, ApiError } from '../../lib/api.ts';
import { browserTimeZone } from '../../lib/format.ts';
import { keys, useAuthOptions, useMe, useSelfSignUp } from '../../lib/queries.ts';

/** The public site's contact form, for those who want an account (sign-up is closed in production). */
export const CONTACT_URL = 'https://maillonfroid.com/#contact';

const tokenFromHash = () => new URLSearchParams(location.hash.slice(1)).get('token');

/** Where to go after signing in: a local path only (never another site). */
function useNext(): string {
  const [params] = useSearchParams();
  const next = params.get('next');
  return next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function AuthLayout() {
  const { t, lang } = useI18n();
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 px-4 py-10">
      <Link to="/" className="mb-6 flex items-center text-2xl">
        <Logo />
      </Link>
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <Outlet />
      </div>
      <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
        <button type="button" className={lang === 'fr' ? 'font-semibold text-slate-800' : 'hover:underline'}
          onClick={() => setLanguage('fr')}>Français</button>
        <button type="button" className={lang === 'en' ? 'font-semibold text-slate-800' : 'hover:underline'}
          onClick={() => setLanguage('en')}>English</button>
        <ThemeToggle labels={{ light: t('shell.themeLight'), dark: t('shell.themeDark') }} />
      </div>
      <p className="mt-2 text-xs text-slate-400">{t('auth.footer')}</p>
    </div>
  );
}

function errorText(t: ReturnType<typeof useI18n>['t'], err: unknown): string {
  if (!(err instanceof ApiError)) return t('errors.network');
  if (err.status === 429) return t('errors.rateLimited');
  if (err.code === 'invalid_credentials') return t('auth.invalidCredentials');
  if (err.code === 'password_breached') return t('auth.breached');
  if (err.code === 'invalid_token') return t('auth.invalidLink');
  if (err.code === 'invalid_code') return t('auth.invalidCode');
  if (err.code === 'invalid_challenge') return t('auth.challengeExpired');
  return err.message;
}

/** After a successful sign-in: the user is cached and the app continues where it was going. */
function useSignedIn() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const next = useNext();
  return (user: Me) => {
    qc.setQueryData(keys.me, user);
    setLanguage(user.locale);
    navigate(next, { replace: true });
  };
}

function SecondFactor({ challenge, onDone }: { challenge: string; onDone: (u: Me) => void }) {
  const { t } = useI18n();
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api<SignInResult>('/auth/sign-in/two-factor', { method: 'POST',
        body: recovery ? { challenge, recovery_code: code.trim().toLowerCase() } : { challenge, totp_code: code.replace(/\s/g, '') } });
      if (r.status === 'signed_in') onDone(r.user);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">{t('auth.twoFactorTitle')}</h1>
      <p className="text-sm text-slate-600">{recovery ? t('auth.recoveryHint') : t('auth.totpHint')}</p>
      {error && <Alert tone="red">{error}</Alert>}
      <Field label={recovery ? t('auth.recoveryCode') : t('auth.totpCode')}>
        {(p) => <Input {...p} value={code} onChange={(e) => setCode(e.target.value)} autoFocus required
          autoComplete="one-time-code" inputMode={recovery ? 'text' : 'numeric'}
          placeholder={recovery ? 'xxxxx-xxxxx' : '123456'} />}
      </Field>
      <Button type="submit" loading={busy}>{t('auth.verify')}</Button>
      <button type="button" className="text-sm text-brand-600 hover:underline" onClick={() => { setRecovery(!recovery); setCode(''); }}>
        {recovery ? t('auth.useTotp') : t('auth.useRecovery')}
      </button>
    </form>
  );
}

export function SignIn() {
  const { t } = useI18n();
  const me = useMe().data;
  const done = useSignedIn();
  const next = useNext();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [mode, setMode] = useState<'password' | 'link' | 'linkSent'>('password');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const selfSignUp = useSelfSignUp();

  useEffect(() => {
    if (me) navigate(next, { replace: true });
  }, [me, navigate, next]);

  if (challenge) return <SecondFactor challenge={challenge} onDone={done} />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'link') {
        await api('/auth/magic-link', { method: 'POST', body: { email } });
        setMode('linkSent');
        return;
      }
      const r = await api<SignInResult>('/auth/sign-in', { method: 'POST', body: { email, password } });
      if (r.status === 'two_factor_required') setChallenge(r.challenge);
      else done(r.user);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'linkSent') {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold">{t('auth.checkInbox')}</h1>
        <p className="text-sm text-slate-600">{t('auth.linkSent', { email })}</p>
        <button type="button" className="text-sm text-brand-600 hover:underline" onClick={() => setMode('password')}>
          {t('auth.backToSignIn')}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">{t('auth.signInTitle')}</h1>
      {error && <Alert tone="red">{error}</Alert>}
      <Field label={t('auth.email')}>
        {(p) => <Input {...p} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />}
      </Field>
      {mode === 'password' && (
        <Field label={t('auth.password')}>
          {(p) => <Input {...p} type="password" autoComplete="current-password" value={password}
            onChange={(e) => setPassword(e.target.value)} required />}
        </Field>
      )}
      <Button type="submit" loading={busy}>{mode === 'password' ? t('auth.signIn') : t('auth.sendLink')}</Button>
      <div className="flex flex-col gap-1.5 text-sm">
        <button type="button" className="text-left text-brand-600 hover:underline"
          onClick={() => setMode(mode === 'password' ? 'link' : 'password')}>
          {mode === 'password' ? t('auth.useLink') : t('auth.usePassword')}
        </button>
        <Link to="/auth/forgot-password" className="text-brand-600 hover:underline">{t('auth.forgot')}</Link>
        {selfSignUp ? (
          <p className="text-slate-600">{t('auth.noAccount')} <Link to={`/auth/sign-up${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}
            className="text-brand-600 hover:underline">{t('auth.signUp')}</Link></p>
        ) : (
          <p className="text-slate-600">{t('auth.noAccount')} <a href={CONTACT_URL} className="text-brand-600 hover:underline">
            {t('auth.contactUs')}</a></p>
        )}
      </div>
    </form>
  );
}

export function SignUp() {
  const options = useAuthOptions();
  if (options.isPending) return <Spinner />;
  if (options.data?.self_sign_up !== true) return <SignUpClosed />;
  return <SignUpForm />;
}

/** Sign-up is closed: the operating team creates the organisations and invites their Owner. */
function SignUpClosed() {
  const { t } = useI18n();
  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-lg font-semibold">{t('auth.closedTitle')}</h1>
      <p className="text-sm text-slate-600">{t('auth.closedBody')}</p>
      <p className="text-sm text-slate-600">{t('auth.closedInvited')}</p>
      <a href={CONTACT_URL} className="inline-flex w-full items-center justify-center rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-on-brand hover:bg-brand-700">
        {t('auth.contactUs')}
      </a>
      <Link to="/auth/sign-in" className="text-sm text-brand-600 hover:underline">{t('auth.haveAccountSignIn')}</Link>
    </div>
  );
}

function SignUpForm() {
  const { t, lang } = useI18n();
  const next = useNext();
  const [form, setForm] = useState({ display_name: '', email: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      await api('/auth/sign-up', { method: 'POST', body: { ...form, locale: lang, timezone: browserTimeZone() } });
      setSent(true);
    } catch (err) {
      if (err instanceof ApiError && err.problem?.errors) setErrors(err.fieldErrors());
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold">{t('auth.checkInbox')}</h1>
        <p className="text-sm text-slate-600">{t('auth.verifySent', { email: form.email })}</p>
        {next !== '/' && <p className="text-sm text-slate-600">{t('auth.signInToContinue')}</p>}
        <Link to={`/auth/sign-in${next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`}
          className="text-sm text-brand-600 hover:underline">{t('auth.backToSignIn')}</Link>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">{t('auth.signUpTitle')}</h1>
      {error && <Alert tone="red">{error}</Alert>}
      <Field label={t('auth.name')} error={errors['/display_name']}>
        {(p) => <Input {...p} autoComplete="name" value={form.display_name} onChange={set('display_name')} required maxLength={200} />}
      </Field>
      <Field label={t('auth.email')} error={errors['/email']}>
        {(p) => <Input {...p} type="email" autoComplete="email" value={form.email} onChange={set('email')} required />}
      </Field>
      <Field label={t('auth.password')} hint={t('auth.passwordHint')} error={errors['/password']}>
        {(p) => <Input {...p} type="password" autoComplete="new-password" minLength={12} maxLength={256} value={form.password}
          onChange={set('password')} required />}
      </Field>
      <Button type="submit" loading={busy}>{t('auth.createAccount')}</Button>
      <p className="text-sm text-slate-600">{t('auth.haveAccount')} <Link to="/auth/sign-in" className="text-brand-600 hover:underline">
        {t('auth.signIn')}</Link></p>
    </form>
  );
}

/** Runs `action` once with the fragment's token (React may render effects twice in development). */
function useTokenAction<T>(action: (token: string) => Promise<T>) {
  const [state, setState] = useState<{ status: 'working' | 'done' | 'error'; value?: T; error?: unknown }>({ status: 'working' });
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = tokenFromHash();
    if (!token) {
      setState({ status: 'error', error: new ApiError(400, null) });
      return;
    }
    history.replaceState(null, '', location.pathname + location.search);
    action(token).then((value) => setState({ status: 'done', value }), (error) => setState({ status: 'error', error }));
  }, [action]);
  return state;
}

export function VerifyEmail() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const me = useMe().data;
  const [verify] = useState(() => (token: string) => api('/auth/verify-email', { method: 'POST', body: { token } }));
  const s = useTokenAction(verify);
  useEffect(() => {
    if (s.status === 'done') void qc.invalidateQueries({ queryKey: keys.me });
  }, [s.status, qc]);
  if (s.status === 'working') return <Spinner label={t('auth.verifying')} />;
  if (s.status === 'error') {
    return <Alert tone="red" title={t('auth.invalidLink')}>{t('auth.verifyFailed')}</Alert>;
  }
  return (
    <div className="flex flex-col gap-3">
      <h1 className="text-lg font-semibold">{t('auth.emailVerified')}</h1>
      <LinkButton to={me ? '/' : '/auth/sign-in'} className="w-full">{me ? t('common.continue') : t('auth.signIn')}</LinkButton>
    </div>
  );
}

export function MagicLink() {
  const { t } = useI18n();
  const done = useSignedIn();
  const [verify] = useState(() => (token: string) => api<SignInResult>('/auth/magic-link/verify', { method: 'POST', body: { token } }));
  const s = useTokenAction(verify);
  useEffect(() => {
    if (s.status === 'done' && s.value?.status === 'signed_in') done(s.value.user);
  }, [s, done]);
  if (s.status === 'working' || s.value?.status === 'signed_in') return <Spinner label={t('auth.signingIn')} />;
  if (s.status === 'error') {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="red" title={t('auth.invalidLink')}>{t('auth.linkFailed')}</Alert>
        <Link to="/auth/sign-in" className="text-sm text-brand-600 hover:underline">{t('auth.backToSignIn')}</Link>
      </div>
    );
  }
  return <SecondFactor challenge={s.value!.status === 'two_factor_required' ? s.value!.challenge : ''} onDone={done} />;
}

/**
 * /auth/impersonate#token=…: a support session, from a link the admin back-office gives staff (single use,
 * 5 minutes). It replaces any session of this browser and opens the organisation it was made for.
 */
export function Impersonate() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open] = useState(() => (token: string) =>
    api<{ status: 'signed_in'; user: Me; org_id: string }>('/auth/impersonate', { method: 'POST', body: { token } }));
  const s = useTokenAction(open);
  useEffect(() => {
    if (s.status !== 'done' || !s.value) return;
    qc.clear();
    qc.setQueryData(keys.me, s.value.user);
    setLanguage(s.value.user.locale);
    navigate(`/o/${s.value.org_id}`, { replace: true });
  }, [s, qc, navigate]);
  if (s.status === 'error') return <Alert tone="red" title={t('auth.invalidLink')}>{t('auth.supportLinkFailed')}</Alert>;
  return <Spinner label={t('auth.openingSupport')} />;
}

/** Where a support session lands when it ends. */
export function SupportEnded() {
  const { t } = useI18n();
  return <Alert tone="blue" title={t('auth.supportEnded')}>{t('auth.supportEndedBody')}</Alert>;
}

export function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api('/auth/password/forgot', { method: 'POST', body: { email } });
      setSent(true);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };
  if (sent) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold">{t('auth.checkInbox')}</h1>
        <p className="text-sm text-slate-600">{t('auth.resetSent', { email })}</p>
        <Link to="/auth/sign-in" className="text-sm text-brand-600 hover:underline">{t('auth.backToSignIn')}</Link>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">{t('auth.forgotTitle')}</h1>
      <p className="text-sm text-slate-600">{t('auth.forgotHint')}</p>
      {error && <Alert tone="red">{error}</Alert>}
      <Field label={t('auth.email')}>
        {(p) => <Input {...p} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />}
      </Field>
      <Button type="submit" loading={busy}>{t('auth.sendResetLink')}</Button>
      <Link to="/auth/sign-in" className="text-sm text-brand-600 hover:underline">{t('auth.backToSignIn')}</Link>
    </form>
  );
}

export function ResetPassword() {
  const { t } = useI18n();
  const [token] = useState(() => {
    const tk = tokenFromHash();
    if (tk) history.replaceState(null, '', location.pathname);
    return tk;
  });
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!token) return <Alert tone="red" title={t('auth.invalidLink')}>{t('auth.linkFailed')}</Alert>;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/password/reset', { method: 'POST', body: { token, password } });
      setDone(true);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };
  if (done) {
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold">{t('auth.passwordChanged')}</h1>
        <p className="text-sm text-slate-600">{t('auth.passwordChangedHint')}</p>
        <LinkButton to="/auth/sign-in" className="w-full">{t('auth.signIn')}</LinkButton>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold">{t('auth.resetTitle')}</h1>
      {error && <Alert tone="red">{error}</Alert>}
      <Field label={t('auth.newPassword')} hint={t('auth.passwordHint')}>
        {(p) => <Input {...p} type="password" autoComplete="new-password" minLength={12} maxLength={256} value={password}
          onChange={(e) => setPassword(e.target.value)} required autoFocus />}
      </Field>
      <Button type="submit" loading={busy}>{t('auth.setPassword')}</Button>
    </form>
  );
}

// ---------------------------------------------------------------- invitations

const INVITE_KEY = 'cck.invitation';
const INVITE_TTL_MS = 7 * 86_400_000;

/** The invitation being accepted, kept across the sign-in or sign-up detour. In local storage, as the
 *  confirmation email of a new account opens in another tab; for as long as an invitation lasts. */
export function pendingInvitation(): string | null {
  try {
    const v = JSON.parse(localStorage.getItem(INVITE_KEY) ?? 'null') as { token: string; at: number } | null;
    return v && Date.now() - v.at < INVITE_TTL_MS ? v.token : null;
  } catch {
    return null;
  }
}

function keepInvitation(token: string | null): void {
  try {
    if (token) localStorage.setItem(INVITE_KEY, JSON.stringify({ token, at: Date.now() }));
    else localStorage.removeItem(INVITE_KEY);
  } catch {
    // storage unavailable
  }
}

/** /invitations/accept#token=… */
export function AcceptInvitation() {
  const { t } = useI18n();
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [token] = useState(() => {
    const tk = tokenFromHash();
    if (tk) keepInvitation(tk);
    return tk ?? pendingInvitation();
  });
  const [state, setState] = useState<'idle' | 'working' | 'error'>('idle');
  const ran = useRef(false);

  useEffect(() => {
    if (location.hash) history.replaceState(null, '', location.pathname);
    if (!me.data || !token || ran.current) return;
    ran.current = true;
    setState('working');
    api<{ org_id: string }>('/invitations/accept', { method: 'POST', body: { token } }).then(async (m) => {
      keepInvitation(null);
      await qc.invalidateQueries({ queryKey: keys.me });
      navigate(`/o/${m.org_id}`, { replace: true });
    }, () => {
      keepInvitation(null);
      setState('error');
    });
  }, [me.data, token, qc, navigate]);

  // Inside AuthLayout (router.tsx), like the other signed-out pages.
  return (
    <>
      {!token && <Alert tone="red" title={t('auth.invalidLink')}>{t('invite.missing')}</Alert>}
      {token && me.isPending && <Spinner />}
      {token && me.data === null && <InvitationSignedOut token={token} />}
      {token && me.data && state !== 'error' && <Spinner label={t('invite.joining')} />}
      {state === 'error' && (
        <div className="flex flex-col gap-3">
          <Alert tone="red" title={t('invite.failed')}>{t('invite.failedBody', { email: me.data?.email ?? '' })}</Alert>
          <Link to="/" className="text-sm text-brand-600 hover:underline">{t('common.continue')}</Link>
        </div>
      )}
    </>
  );
}

/**
 * Signed out: what the invitation is for, and an account created on the spot for the invited address (the link
 * proves it). Signing in instead keeps the token for after the detour.
 */
function InvitationSignedOut({ token }: { token: string }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const info = useQuery({ queryKey: ['invitation', token], retry: false, staleTime: Infinity,
    queryFn: () => api<InvitationInfo>('/auth/invitation', { method: 'POST', body: { token } }) });
  const [form, setForm] = useState({ display_name: '', password: '', locale: lang });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [busy, setBusy] = useState(false);
  const invalid = info.error instanceof ApiError && (info.error.code === 'invitation_invalid' || info.error.status === 404);
  useEffect(() => {
    if (invalid) keepInvitation(null);
  }, [invalid]);

  const signIn = (
    <LinkButton to="/auth/sign-in?next=/invitations/accept" variant={exists ? 'primary' : 'secondary'} className="w-full">
      {t('auth.signIn')}
    </LinkButton>
  );
  if (info.isPending) return <Spinner />;
  if (invalid) {
    return <Alert tone="red" title={t('invite.invalidTitle')}>{t('invite.invalidBody')}</Alert>;
  }
  if (info.isError) {
    // Unreachable API or rate limit: the older flow still works.
    return (
      <div className="flex flex-col gap-3">
        <h1 className="text-lg font-semibold">{t('invite.title')}</h1>
        <Alert tone="red">{errorText(t, info.error)}</Alert>
        {signIn}
      </div>
    );
  }
  const i = info.data;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setErrors({});
    try {
      const r = await api<SignInResult>('/auth/invitation-sign-up', { method: 'POST',
        body: { token, password: form.password, display_name: form.display_name, locale: form.locale, timezone: browserTimeZone() } });
      if (r.status === 'signed_in') {
        setLanguage(r.user.locale);
        // AcceptInvitation accepts it as soon as the user is known.
        qc.setQueryData(keys.me, r.user);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'account_exists') setExists(true);
      else if (err instanceof ApiError && err.code === 'invitation_invalid') {
        keepInvitation(null);
        await qc.invalidateQueries({ queryKey: ['invitation', token] });
      } else {
        if (err instanceof ApiError && err.problem?.errors) setErrors(err.fieldErrors());
        setError(errorText(t, err));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold">{t('invite.joinTitle', { org: i.org_name })}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {t('invite.forRole', { role: t(`role.${i.role}`) })}
        </p>
        <p className="mt-1 text-sm text-slate-600">{t('invite.forAddress', { email: i.email })}</p>
      </div>
      {exists ? (
        <>
          <Alert tone="blue" title={t('invite.accountExists')}>{t('invite.accountExistsBody', { email: i.email })}</Alert>
          {signIn}
        </>
      ) : (
        <>
          <form onSubmit={submit} className="flex flex-col gap-4">
            {error && <Alert tone="red">{error}</Alert>}
            <Field label={t('auth.email')}>
              {(p) => <Input {...p} type="email" value={i.email} readOnly autoComplete="username" className="bg-slate-50" />}
            </Field>
            <Field label={t('auth.name')} error={errors['/display_name']}>
              {(p) => <Input {...p} autoComplete="name" value={form.display_name} required maxLength={200} autoFocus
                onChange={(e) => setForm({ ...form, display_name: e.target.value })} />}
            </Field>
            <Field label={t('auth.password')} hint={t('auth.passwordHint')} error={errors['/password']}>
              {(p) => <Input {...p} type="password" autoComplete="new-password" minLength={12} maxLength={256} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required />}
            </Field>
            <Field label={t('invite.language')}>
              {(p) => (
                <Select {...p} value={form.locale} onChange={(e) => {
                  const locale = e.target.value === 'en' ? 'en' : 'fr';
                  setForm({ ...form, locale });
                  setLanguage(locale);
                }}>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </Select>
              )}
            </Field>
            <Button type="submit" loading={busy}>{t('invite.createAndJoin')}</Button>
          </form>
          <div className="flex flex-col gap-2 border-t border-slate-100 pt-3">
            <p className="text-sm text-slate-600">{t('auth.haveAccount')}</p>
            {signIn}
          </div>
        </>
      )}
    </div>
  );
}
